<template>
	<CardBase 
		ref="card" 
		:app="app"
		:card="card" 
		:loading="loading" 
		:message="message"
		:actions="actions" 
		@action="handleAction">

		<template #title>
			Select options for watertight processing.
		</template>

		<template v-if="isOutage || isLegacy" #status>
			<AtomStatus 
                :level="statusLevel"
                :status="statusMessage" />
		</template>

		<template #content>
			<div class="card-watertight">

				<div
					class="scrollable"
					ref="optionsScrollable"
					@wheel.stop
					@touchmove.stop>

					<!-- Quality -->
					<div class="group">
                        <div class="heading">
                            <div class="name">
                                Remesh
                            </div>
                            <div class="value">
                            </div>
                        </div>

                        <div class="option">
							<div class="name">
								<v-tooltip location="right" :text="remeshMethodText">
									<template #activator="{ props }">
										<div class="name-inner" v-bind="props">
											<span>Method</span>
                                            <v-icon icon="fa-solid fa-info-circle"></v-icon>
										</div>
									</template>
								</v-tooltip>
							</div>
							<div class="value">
                                <v-btn-toggle 
                                    mandatory 
                                    class="toggle" 
                                    color="color1" 
                                    elevation="0" 
                                    density="default"
                                    v-model="remeshMethod" 
                                    :disabled="isOutage && !isAdministrator">
                                    <v-btn 
                                        class="toggle-button" 
                                        elevation="0"
                                        :value="keyMethodVoxel"  
                                        :disabled="isOutage && !isAdministrator">
                                        <span class="method-icon voxel-icon" aria-hidden="true"></span>
                                        <span>Voxel</span>
                                    </v-btn>
                                    <v-btn 
                                        class="toggle-button" 
                                        elevation="0"
                                        :value="keyMethodTetrahedron" 
                                        :disabled="isOutage && !isAdministrator">
                                        <span class="method-icon tetrahedron-icon" aria-hidden="true"></span>
                                        <span>Tetrahedron</span>
                                    </v-btn>
                                </v-btn-toggle>
                            </div>
                        </div>

                        <div class="option">
							<div class="name">
								<v-tooltip location="right" :text="remeshQualityText">
									<template #activator="{ props }">
										<div class="name-inner" v-bind="props">
											<span>Quality</span>
                                            <v-icon icon="fa-solid fa-info-circle"></v-icon>
										</div>
									</template>
								</v-tooltip>
							</div>
							<div class="value">
                                <v-btn-toggle 
                                    mandatory 
                                    class="toggle" 
                                    color="color1" 
                                    elevation="0" 
                                    density="default"
                                    v-model="remeshQuality" 
                                    :disabled="isOutage && !isAdministrator">
                                    <v-btn 
                                        class="toggle-button" 
                                        elevation="0"
                                        :value="keyQualityLow"  
                                        :disabled="isOutage && !isAdministrator">
                                        <v-icon icon="fa-solid fa-star-half-stroke"></v-icon>
                                        <span>Low</span>
                                    </v-btn>
                                    <v-btn 
                                        class="toggle-button" 
                                        elevation="0"
                                        :value="keyQualityHigh" 
                                        :disabled="isOutage && !isAdministrator">
                                        <v-icon icon="fa-solid fa-star"></v-icon>
                                        <span>High</span>
                                    </v-btn>
                                    <v-btn 
                                        class="toggle-button" 
                                        elevation="0"
                                        :value="keyQualityUltra" 
                                        :disabled="isOutage && !isAdministrator"> 
                                        <v-icon icon="fa-regular fa-face-grin-stars"></v-icon>
                                        <span>Ultra</span>
                                    </v-btn>
                                </v-btn-toggle>
                            </div>
                        </div>

						<div class="option" style="margin-top:5px;">
							<div class="name">
							</div>
							<div class="value" style="font-size:0.9rem;color:rgb(var(--v-theme-tertiary))" v-html="remeshSummary"></div>
						</div>
                    </div>


					<!-- Polygons -->
					<div class="group">
						<div class="heading">
							<div class="name">
								Polygons
							</div>
							<div class="value">
							</div>
						</div>
						<div class="option">
							<div class="name">
								<v-tooltip location="right" :text="polygonMaximumText">
									<template #activator="{ props }">
										<div class="name-inner" v-bind="props">
											<span>Maximum</span>
											<v-icon icon="fa-solid fa-info-circle"></v-icon>
										</div>
									</template>
								</v-tooltip>
							</div>
							<div class="value">
								<v-slider 
									class="slider"
									tick-size="2" 
									show-ticks="always" 
									v-model="polygonMaximum" 
									:min="polygons10"
									:max="polygons100" 
									:step="step"
									:ticks="polygonMaximumTicks">
									<template v-slot:append>
										<v-text-field 
											hide-details
											class="slider-field" 
											color="color1"
											density="compact"
											variant="outlined" 
											v-model="polygonMaximum" 
											@keydown.stop
											@keypress.stop
											@keyup.stop>
										</v-text-field>
									</template>
								</v-slider>
							</div>
						</div>
					</div>

                    <div v-if="texture" class="warning">
                        <v-icon color="warning" icon="fa-solid fa-triangle-exclamation"></v-icon>&nbsp;
                        <span>Texture will be lost after watertight processing.</span>
                    </div>
				</div>

			</div>
		</template>

		<template #side>
			<div class="asset">
				<div class="heading">Model</div>
				<div class="viewer">
					<ViewerImage :asset="primaryAsset" />
				</div>
			</div>
		</template>

	</CardBase>
</template>

<script>
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import QUALITY from '@generio-ai/library-legacy/constants/quality'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { ViewerImage } from '@generio-ai/library-legacy/components/viewer'
import ACTION from '@/constants/action.js'
import CARD from '@/constants/card.js'
import AtomStatus from '@/components/common/atom/AtomStatus.vue'
import CardBase from '@/components/flow/cards/CardBase.vue'

export default {
	name: 'CardWatertight',
	components: {
		AtomStatus,
		ViewerImage,
		CardBase
	},
	props: {
		app: {
            type: String,
            required: true
        },
        card: {
            type: Object,
            required: true
        }
    },
	data() {
		return {
			loading: false,
			message: '',
			healthy: {
				model_optimize: true,
			}
		}
	},
	computed: {
		...mapGetters('auth', [
			'isAdministrator'
		]),
		...mapGetters('flows', [
			'isActiveByIndex',
			'isLastByIndex',
			'getPrimaryCardForCard'
        ]),

		keyMethodVoxel() {
			return 'voxel'
		},
		keyMethodTetrahedron() {
			return 'tetrahedron'
		},

        keyQualityLow() {
            return QUALITY.LOW
        },
        keyQualityHigh() {
            return QUALITY.HIGH
        },
        keyQualityUltra() {
            return QUALITY.ULTRA
        },

		isOutage() {
			return !this.healthy.model_optimize
		},
		isLegacy() {
			return this.version < 10
		},

		isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},
		isLastCard() {
            return this.isLastByIndex(this.app, this.index)
        },

		index() {
            return this.card.index
        },
		nextIndex(){
			return this.card.index + 1
		},
		category() {
			return this.card.category
		},
		prompt() {
			return this.card.prompt
		},

		primaryCard() {
			return this.getPrimaryCardForCard(this.app, this.card, false)
		},
		primaryAssets() {
			return this.primaryCard?.assets
		},
		primaryAsset() {
			return this.primaryCard?.getSelectedOrFirstAsset()
		},
		primaryFile() {
			return this.primaryAsset?.files?.[ASSETS.FILES.KEY.DEFAULT]
		},
		primaryDetails() {
			return this.primaryFile?.details
		},

		version() {
			return this.primaryAsset?.additional?.version || 0
		},

        texture() {
            return this.primaryDetails?.texture || false
        },
		polygons100() {
			return 1_000_000
		},
		polygons75() {
			return 750_000
		},
		polygons50() {
			return 500_000
		},
		polygons25() {
			return 250_000
		},
		polygons10() {
			return 100_000
		},
		step() {
			return Math.floor(this.polygons100 / 20)
		},

		actions() {
			if ((this.isOutage && !this.isAdministrator) || this.isLegacy) return []
			return [
				ACTION.MAKE_WATERTIGHT
			]
		},

		statusLevel() {
			if (this.isLegacy) return 'warning'
            if (this.isOutage) return 'error'
            return 'healthy'
        },
        statusMessage() {
			if (this.isLegacy) return "Model version is outdated and not supported for watertight processing."
            if (this.isOutage) return "Watertight processing service is currently unavailable."
            return "Watertight processing service is healthy."
        },

		remeshMethod: {
			get() {
                const rawMethod = this.card.option?.remeshMethod
				return rawMethod ?? this.keyMethodVoxel
			},
			set(value) {
				if (!this.card?.option) this.card.option = {}
				this.card.option.remeshMethod = value
			}
		},
		remeshQuality: {
			get() {
                const rawQuality = this.card.option?.remeshQuality
				return rawQuality ?? this.keyQualityLow
			},
			set(value) {
				if (!this.card?.option) this.card.option = {}
				this.card.option.remeshQuality = value
			}
		},
		polygonMaximum: {
			get() {
				const rawMaximum = this.card.option?.polygonMaximum
				let value = rawMaximum ?? this.polygons50
				if (value < this.polygons10) {
					value = this.polygons10
					if (!this.card?.option) this.card.option = {}
					this.card.option.polygonMaximum = value
				}
				return value
			},
			set(value) {
				if (!this.card?.option) this.card.option = {}
				const numericValue = Number(value)
				const safeValue = Number.isFinite(numericValue) ? Math.max(this.polygons10, Math.round(numericValue)) : this.polygons10
				this.card.option.polygonMaximum = safeValue
			}
		},

        remeshMethodText() {
			return 'method used for remeshing the model geometry'
		},
        remeshQualityText() {
			return 'quality used for remeshing the model geometry'
		},
		remeshSummary() {
			if (this.remeshMethod === this.keyMethodVoxel) {
				let gridResolution = null
				if (this.remeshQuality === this.keyQualityLow) gridResolution = 128
				if (this.remeshQuality === this.keyQualityHigh) gridResolution = 256
				if (this.remeshQuality === this.keyQualityUltra) gridResolution = 512
				return `Voxel-based remeshing with grid resolution:&nbsp;<strong>${gridResolution}³</strong>`
			}
			if (this.remeshMethod === this.keyMethodTetrahedron) {
				let decimate = null
				if (this.remeshQuality === this.keyQualityLow) decimate = 0.95
				if (this.remeshQuality === this.keyQualityHigh) decimate = 0.9
				if (this.remeshQuality === this.keyQualityUltra) decimate = 0.8
				return `Tetrahedron-based remeshing with decimate:&nbsp;<strong>${decimate}</strong>`
			}
			return ``
		},

		polygonMaximumText() {
			return 'upper polygon count limit for the watertight model'
		},
		polygonMaximumTicks() {
			return {
				[this.polygons10]: '100k',
				[this.polygons25]: '250k',
				[this.polygons50]: '500k',
				[this.polygons75]: '750k',
				[this.polygons100]: '1M'
			}
		}
	},
	methods: {
		...mapActions('publics', [
			'fetchStatus'
		]),
		...mapActions('flows', [
			'newTransaction',
			'commitPrimitiveAsset',
			'fetchOptimizeModelWatertight',
		]),
		async updateStatus() 
		{
			// default is that the services are healthy
			this.healthy = {
				model_optimize: true,
			}

			try
			{
				// get status
				const status = await this.fetchStatus()

				// has healthy services
				if (status?.healthy) 
				{
					if (status.healthy.model_optimize === false) this.healthy.model_optimize = false
				}
			}
			catch (error)
			{
				// silent fail - if status check does not work, we still want to proceed
			}
		},		
		async handleAction(action) {
			if (action === null || action === undefined) return

			switch(action.id) 
			{
				case ACTION.MAKE_WATERTIGHT.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitPrimitiveAsset(),
						taskFetch: this.fetchOptimizeModelWatertight({
							component: CARD.MODEL,
							remeshMethod: this.remeshMethod,
                            remeshQuality: this.remeshQuality,
							polygonMaximum: this.polygonMaximum
						})
					})
					break
			}
		}
	},
	mounted() {
		this.updateStatus()
	}
}
</script>

<style>
.v-slider-thumb__label {
	background-color: rgb(var(--v-theme-darkbase));
	color: rgb(var(--v-theme-primary));
}

.slider-field {
	width: 80px;
}

.slider-field .v-field__input {
	padding: 8px !important;
	text-align: center;
}

.toggle .v-btn.v-btn--disabled {
    opacity: 0.5;
}
</style>

<style scoped>
.card-watertight {
	width: 100%;
	height: 100%;
	max-height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

.card-watertight .scrollable {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	overflow-y: auto;
	align-self: stretch;
	max-height: 100%;
	min-height: 0;
	padding: 2rem;
}

.card-watertight .group {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}
.card-watertight .group .heading {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 1.5rem;
}

.card-watertight .group .heading .name {
	min-width: 180px;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	font-size: 1.1rem;
	font-weight: bold;
}

.card-watertight .group .heading .value {
	flex-grow: 1;
	border-bottom: 1px solid rgb(var(--v-theme-shadow1));
}

.card-watertight .group .option {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 1.5rem;
}

.card-watertight .group .option .name {
	min-width: 180px;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	font-size: 1.0rem;
}

.card-watertight .group .option .name .name-inner {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 0.3rem;
}

.card-watertight .group .option .name .name-inner .v-icon {
	color: rgb(var(--v-theme-tertiary));
	font-size: 0.8rem;
	margin-top: 0.1rem;
}

.card-watertight .group .option .value {
	flex-grow: 1;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
}

.asset {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
}

.asset .viewer {
	width: 100%;
	display: block;
}

.card-watertight .toggle {
    max-height: 40px !important;
    min-height: 40px !important;
	width: 100%;
	display: flex;
	flex-direction: row;
	border: 1px solid rgb(var(--v-theme-lightbase));
}

.card-watertight .toggle-button {
	flex: 1;
	flex-shrink: unset;
    max-height: 38px !important;
    min-height: 38px !important;
	background-color: rgb(var(--v-theme-darkbase));
    border-radius: 0px !important;
}

.card-watertight .method-icon {
	display: inline-block;
	width: 1.25em;
	height: 1.25em;
	flex: 0 0 auto;
	background-color: currentColor;
	-webkit-mask-position: center;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-size: contain;
	mask-position: center;
	mask-repeat: no-repeat;
	mask-size: contain;
}

.card-watertight .method-icon + span {
	padding-inline-start: 0.25rem;
}

.card-watertight .voxel-icon {
	-webkit-mask-image: url('/icon/voxel.svg');
	mask-image: url('/icon/voxel.svg');
}

.card-watertight .tetrahedron-icon {
	-webkit-mask-image: url('/icon/tetrahedron.svg');
	mask-image: url('/icon/tetrahedron.svg');
}

.card-watertight .slider {
	margin-left: 15px;
	margin-right: 15px;
}

.card-watertight .warning {
    color: rgb(var(--v-theme-warning));
    font-size: 1.2rem;
}

@media (max-width: 800px) {
	.card-watertight .group .heading .name {
		min-width: 150px;
	}

	.card-watertight .group .option .name {
		min-width: 150px;
	}
}

@media (max-width: 600px) {
	.card-watertight .group {
		gap: 1rem;
	}

	.card-watertight .group .heading .name {
		min-width: unset;
	}

	.card-watertight .group .option {
		flex-direction: column;
		gap: 0.5rem;
	}

	.card-watertight .group .option .name {
		width: 100%;
		min-width: unset;
		justify-content: flex-start;
	}

	.card-watertight .group .option .value {
		width: 100%;
		min-width: unset;
		justify-content: flex-start;
	}
}
</style>
